import { faPenToSquare } from '@fortawesome/free-solid-svg-icons/faPenToSquare';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Button } from '../../../components/Button';
import { Flex } from '../../../components/Flex';
import { Text } from '../../../components/Text';
import { TextWithTruncated } from '../../../components/TextWithTruncated';
import { AtiraLinkThemes } from '../../../model/atira-link/types/AtiraLinkThemes.enum';
import { AtiraLinkTheme } from '../../../model/theme/AtiraLinkTheme';
import { AtiraLinkThemeStatus } from '../../../model/theme/type/AtiraLinkThemeStatus.enum';
import { Rounded } from '../../../theme/Rounded';
import { Shadow } from '../../../theme/Shadow';
import { Spacing } from '../../../theme/Spacing';
import { ThemeType } from '../../../theme/Theme';
import { Tooltip } from '../../../utils/local-antd';
import { PhonePreviewIframe } from '../../my-link/components/PhonePreviewIframe';

const StatusContainer = styled(Flex)`
  border-radius: ${Rounded.circle};
  width: 12px;
  height: 14px;
  background-color: ${({ theme, backgroundColor }) =>
    theme[backgroundColor || 'success']};
`;

const Container = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.s};
  max-height: 25rem;
  height: 25rem;
  background: ${({ theme }) => `
  linear-gradient(0deg, ${theme.accent}, ${theme.main})
  `};
  padding: ${Spacing.s};
  border-radius: ${Rounded.md};
  box-shadow: ${Shadow.MD};
`;

const ThemeName = styled(Text)`
  text-align: center;
  font-weight: bold;
`;

const ManageButton = styled(Button)`
  background-color: ${({ theme }) => theme.sub};
  color: ${({ theme }) => theme.main};
  margin: auto;
  width: 100%;

  &:hover {
    background-color: ${({ theme }) => theme.subHover};
  }
`;

const IframeWrapper = styled(Flex)`
  height: 100%;
  margin: auto;

  iframe {
    width: 100%;
    height: 100%;
  }

  button {
    inset: 0;
    border-radius: 0;
  }
`;

const StatusToColor: Record<AtiraLinkThemeStatus, keyof ThemeType> = {
  [AtiraLinkThemeStatus.PENDING]: 'warning',
  [AtiraLinkThemeStatus.ACTIVE]: 'success',
  [AtiraLinkThemeStatus.INACTIVE]: 'danger',
};

const StatusToText: Record<AtiraLinkThemeStatus, string> = {
  [AtiraLinkThemeStatus.PENDING]: 'admin.theme.status.pending',
  [AtiraLinkThemeStatus.INACTIVE]: 'admin.theme.status.inactive',
  [AtiraLinkThemeStatus.ACTIVE]: 'admin.theme.status.active',
};

type AdminThemeCardProps = {
  theme: AtiraLinkTheme;
  onManage: ATVoidFunction<AtiraLinkTheme>;
  onSelectTheme: ATVoidFunction<AtiraLinkTheme>;
};

export const AdminThemeCard: React.FC<AdminThemeCardProps> = ({
  theme,
  onManage,
  onSelectTheme,
}) => {
  const { t } = useTranslation();

  const { name, description, status } = theme;

  return (
    <Flex flexDirection="column" gap="s">
      <Container>
        <Tooltip title={t(StatusToText[status])}>
          <StatusContainer backgroundColor={StatusToColor[status]} />
        </Tooltip>

        <ThemeName color="onMain">{name}</ThemeName>

        {description ? (
          <TextWithTruncated
            textProps={{ color: 'onMain', align: 'center' }}
            length={15}
          >
            {description}
          </TextWithTruncated>
        ) : null}

        <IframeWrapper>
          <PhonePreviewIframe
            theme={name as AtiraLinkThemes}
            key={name}
            onPreview={() => onSelectTheme(theme as AtiraLinkTheme)}
          />
        </IframeWrapper>

        <ManageButton onClick={() => onManage(theme)} icon={faPenToSquare}>
          {t('common.manage')}
        </ManageButton>
      </Container>
    </Flex>
  );
};
